import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtIDR, fmtDate } from "@/lib/format";
import { Plus, Trash2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Tax Refund", "Kardus / Packing", "Bank Fee", "Transport Lokal", "Biaya Lain"];

export default function CostProfit() {
  const { event, eventId } = useOutletContext();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [costs, setCosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: CATEGORIES[0], amount: 0, note: "" });
  const canCost = ["shopper", "finance", "admin"].some((r) => user.roles.includes(r));

  const load = async () => {
    const [s, c] = await Promise.all([api.get(`/events/${eventId}/summary`), api.get(`/events/${eventId}/costs`)]);
    setSummary(s.data); setCosts(c.data);
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    try { await api.post(`/events/${eventId}/costs`, { ...form, amount: Number(form.amount) }); toast.success("Biaya ditambahkan"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/costs/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const kpis = summary ? [
    { label: "Omzet (Order Valid)", value: fmtIDR(summary.revenue) },
    { label: "Penjualan Stok Sisa", value: fmtIDR(summary.stock_sales_revenue || 0), color: "text-emerald-700" },
    { label: "Top-up", value: fmtIDR(summary.topup), color: "text-rose-600" },
    { label: "Refund", value: summary.refund ? "-" + fmtIDR(summary.refund).slice(3) : fmtIDR(0), color: "text-emerald-600" },
    { label: "Total Penerimaan", value: fmtIDR(summary.total_revenue) },
    { label: "COGS (Modal Aktual)", value: fmtIDR(summary.cogs) },
    { label: "Ongkir Box Asal→SG", value: fmtIDR(summary.box_shipping_origin || 0), color: "text-slate-700" },
    { label: "Ongkir Box SG→Indo", value: fmtIDR(summary.box_shipping_indo || 0), color: "text-slate-700" },
    { label: "Biaya Operasional (total)", value: fmtIDR(summary.op_cost) },
    { label: "Biaya Lainnya Order (pass-through)", value: fmtIDR(summary.passthrough_fees || 0), color: "text-slate-500" },
    { label: "Ongkir ke Customer (ditagih, pass-through)", value: fmtIDR(summary.customer_shipping_billed || 0), color: "text-slate-500" },
  ] : [];
  const unfCols = [
    { key: "name", label: "Barang", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> },
    { key: "sku", label: "SKU", mono: true },
    { key: "customer_name", label: "Customer" },
    { key: "qty", label: "Dipesan", mono: true },
    { key: "bought_qty", label: "Dibeli", mono: true },
    { key: "missing_qty", label: "Kurang", mono: true, render: (r) => <span className="font-bold text-rose-600">{r.missing_qty}</span> },
    { key: "value", label: "Nilai (IDR)", mono: true, render: (r) => fmtIDR(r.value), raw: (r) => r.value },
    { key: "paid_at", label: "Lunas", render: (r) => r.paid_at ? fmtDate(r.paid_at) : "-", raw: (r) => r.paid_at || "" },
  ];
  const picCols = [
    { key: "pic_name", label: "PIC", render: (r) => <span className="font-semibold text-slate-900">{r.pic_name}</span> },
    { key: "orders", label: "Jumlah Order (lunas)", mono: true },
    { key: "revenue", label: "Omzet", mono: true, render: (r) => <span className="font-semibold">{fmtIDR(r.revenue)}</span>, raw: (r) => r.revenue },
  ];

  const columns = [
    { key: "category", label: "Kategori", render: (r) => <span className="font-semibold text-slate-900">{r.category}</span> },
    { key: "amount", label: "Nominal (IDR)", mono: true, render: (r) => fmtIDR(r.amount), raw: (r) => r.amount },
    { key: "note", label: "Catatan" },
    { key: "created_at", label: "Tanggal", render: (r) => fmtDate(r.created_at), raw: (r) => fmtDate(r.created_at) },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => canCost && <button className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`del-cost-${r.id.slice(0,8)}`} onClick={() => del(r.id)}><Trash2 className="w-4 h-4" /></button> },
  ];

  return (
    <div>
      <PageHeader title="Biaya & Profit Final Event" subtitle="Biaya bisa ditambah walau event sudah close. Profit = total penerimaan − COGS − biaya operasional.">
        {canCost && <Button data-testid="btn-add-cost" onClick={() => setOpen(true)} className="gap-1.5 bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4" /> Tambah Biaya</Button>}
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {kpis.map((k) => (
          <div key={k.label} className="p-3.5 bg-white border border-slate-200 rounded-lg" data-testid={`kpi-${k.label}`}>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{k.label}</div>
            <div className={`text-lg font-black tracking-tight font-mono tabular-nums mt-1 ${k.color || "text-slate-950"}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {summary && (
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          <div className={`p-5 rounded-lg text-white ${summary.profit >= 0 ? "bg-emerald-700" : "bg-rose-700"}`} data-testid="kpi-profit">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider opacity-80">Profit Final Event</div>
                <div className="text-3xl font-black tracking-tight font-mono tabular-nums mt-1">{fmtIDR(summary.profit)}</div>
              </div>
              {summary.profit >= 0 ? <TrendingUp className="w-10 h-10 opacity-60" /> : <TrendingDown className="w-10 h-10 opacity-60" />}
            </div>
          </div>
          <div className="p-5 rounded-lg bg-slate-900 text-white" data-testid="kpi-margin">
            <div className="text-xs font-medium uppercase tracking-wider opacity-80">Profit Margin</div>
            <div className="text-3xl font-black tracking-tight font-mono tabular-nums mt-1">{summary.margin.toFixed(1)}%</div>
            <div className="text-xs opacity-70 mt-1">{summary.paid_orders} order tervalidasi</div>
          </div>
        </div>
      )}

      {summary?.stock_items?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4" data-testid="stock-summary">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="font-bold text-slate-900">Stok Sisa Event (barang dibeli melebihi order)</h3>
            <span className="text-sm text-slate-600">{summary.stock_qty} pcs · nilai modal <span className="font-mono font-semibold text-slate-900">{fmtIDR(summary.stock_value)}</span></span>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase text-slate-500 border-b border-amber-200"><th className="text-left py-1">Item</th><th className="text-right">Stok</th><th className="text-right">Modal/pcs</th><th className="text-right">Nilai</th></tr></thead>
            <tbody>{summary.stock_items.map((s) => (
              <tr key={s.catalog_item_id} className="border-b border-amber-100"><td className="py-1.5">{s.name} <span className="font-mono text-[10px] text-slate-400">{s.sku}</span></td><td className="text-right font-mono font-semibold">{s.stock_qty}</td><td className="text-right font-mono">{fmtIDR(Math.round(s.avg_cost_idr))}</td><td className="text-right font-mono">{fmtIDR(s.stock_value_idr)}</td></tr>
            ))}</tbody>
          </table>
          <p className="text-[11px] text-slate-500 mt-2">Nilai stok sudah termasuk dalam COGS. Jika stok terjual di luar event, catat sebagai pendapatan terpisah.</p>
        </div>
      )}

      {summary && (
        <div className="mb-6" data-testid="unfulfilled-section">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="font-bold text-slate-900">Sudah Dibayar, Belum Dibelikan Shopper</h3>
            <span className="text-sm text-slate-500">{summary.unfulfilled.length} baris · {summary.unfulfilled.reduce((s, u) => s + u.missing_qty, 0)} pcs · nilai <span className="font-mono font-semibold text-rose-600">{fmtIDR(summary.unfulfilled.reduce((s, u) => s + u.value, 0))}</span></span>
          </div>
          <DataTable testId="unfulfilled-table" columns={unfCols} data={summary.unfulfilled} searchKeys={["name", "sku", "customer_name"]} exportName={`belum-dibeli-${event?.code}`} emptyText="Semua barang dari order lunas sudah dibelikan." />
        </div>
      )}
      {summary && (
        <div className="mb-6" data-testid="pic-section">
          <h3 className="font-bold text-slate-900 mb-2">Laporan PIC</h3>
          <DataTable testId="pic-table" columns={picCols} data={summary.pic_report} searchKeys={["pic_name"]} exportName={`pic-${event?.code}`} emptyText="Belum ada order lunas." />
        </div>
      )}

      <h3 className="font-bold text-slate-900 mb-2">Biaya Operasional Manual</h3>
      <DataTable testId="costs-table" columns={columns} data={costs} searchKeys={["category", "note"]} exportName={`biaya-${event?.code}`} emptyText="Belum ada biaya." />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Biaya Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="cost-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nominal (IDR)</Label><Input data-testid="cost-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><Label>Catatan</Label><Input data-testid="cost-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="save-cost" onClick={add} className="bg-slate-900 hover:bg-slate-800">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
